/**
 * Visibility Filter Slice
 *
 * Manages which locations are visible based on filters (date, type, bounds, search).
 * Maintains a lightweight Set of visible location IDs for O(1) lookups.
 */

import type { Location } from '@/types/location';
import type { MapFilters } from '@/types/map';
import type { MapBounds, SearchPoint } from '../helpers';
import {
  calculateDistance,
  saveSearchPointToStorage,
} from '../helpers';

/**
 * Minimum radius in miles for viewport-based filtering.
 * Ensures locations remain visible even at high zoom levels.
 */
const MIN_VIEWPORT_RADIUS_MILES = 50;

export interface VisibilityFilterSlice {
  visibleLocationIds: Set<number>;
  mapBounds: MapBounds | null;
  searchPoint: SearchPoint | null;
  /**
   * When a name search is active, the set of location IDs that matched the
   * query by name. The visible list is restricted to this set (a multi-result
   * search), independent of the map viewport and the searchPoint radius. Null
   * when no name search is active.
   */
  searchMatchIds: Set<number> | null;
  filters: MapFilters;

  // Actions
  setMapBounds: (bounds: MapBounds | null) => void;
  setSearchPoint: (point: SearchPoint | null) => void;
  /**
   * Activate a name search: restrict the list to `matchIds` and use `origin`
   * as the proximity center for sorting and for panning to the nearest match.
   */
  setNameSearch: (matchIds: Set<number>, origin: SearchPoint | null) => void;
  updateFilters: (filters: Partial<MapFilters>) => void;
  clearFilters: () => void;
  updateVisibleLocations: () => void;

  // Computed
  getVisibleLocations: () => Location[];
}

/**
 * Default filter state
 */
const defaultFilters: MapFilters = {
  date: null,
  locationType: null,
  searchQuery: "", // URL params are source of truth, not localStorage (prevents stale URL sync)
};

export const createVisibilityFilterSlice = (
  set: any,
  get: any
): VisibilityFilterSlice => ({
  // Initial state
  visibleLocationIds: new Set<number>(),
  mapBounds: null, // Map viewport bounds (set when user pans/zooms)
  searchPoint: null, // Don't restore from localStorage - causes stale filtered results on load
  searchMatchIds: null, // No name search active on load
  filters: defaultFilters,

  /**
   * Set map viewport bounds for filtering locations and recalculate visibility
   *
   * @remarks
   * Called when user pans or zooms the map.
   * Bounds filtering takes precedence over search radius.
   *
   * @param bounds - Map viewport bounds (null to clear)
   */
  setMapBounds: (bounds) => {
    set({ mapBounds: bounds });
    // Recalculate visible locations
    get().updateVisibleLocations();
  },

  /**
   * Set search point for proximity filtering and recalculate visibility
   *
   * @remarks
   * When a new search is performed, clear mapBounds so that
   * search radius filtering takes precedence over viewport bounds.
   *
   * @param point - Search point coordinates and radius (null to clear)
   */
  setSearchPoint: (point) => {
    // A point/locality search supersedes any active name search.
    set({ searchPoint: point, mapBounds: null, searchMatchIds: null });
    // Persist to localStorage
    saveSearchPointToStorage(point);

    // Recalculate visible locations
    get().updateVisibleLocations();
  },

  /**
   * Activate a name search and recalculate visibility
   *
   * @remarks
   * Used when the query matches pickup locations by name (e.g. "Lowe's"),
   * which can match many locations. The list is restricted to `matchIds`
   * rather than to a single point's radius, and `origin` (the user's location
   * or the nearest match) becomes the proximity center for sorting. mapBounds
   * is cleared; the full match set stays visible until the search is cleared or
   * replaced (it is not narrowed by panning).
   *
   * @param matchIds - IDs of locations whose names matched the query
   * @param origin - Proximity center for sorting (null to leave unset)
   */
  setNameSearch: (matchIds, origin) => {
    set({ searchMatchIds: matchIds, searchPoint: origin, mapBounds: null });
    // Persist origin to localStorage (same as a point search)
    saveSearchPointToStorage(origin);

    // Recalculate visible locations
    get().updateVisibleLocations();
  },

  /**
   * Update filters (partial update) and recalculate visibility
   *
   * @param filters - Filter values to update
   */
  updateFilters: (filters) => {
    set((state: VisibilityFilterSlice) => ({
      filters: {
        ...state.filters,
        ...filters,
      },
    }));

    // Recalculate visible locations if filters affect visibility
    if (filters.date !== undefined || filters.locationType !== undefined) {
      get().updateVisibleLocations();
    }
  },

  /**
   * Clear all filters and recalculate visibility
   */
  clearFilters: () => {
    set({
      filters: {
        date: null,
        locationType: null,
        searchQuery: '',
      },
      searchMatchIds: null,
    });

    // Recalculate visible locations
    get().updateVisibleLocations();
  },

  /**
   * Update visible locations based on current filters
   *
   * @remarks
   * Centralized filtering logic that updates the visibleLocationIds Set.
   * Called automatically by filter actions (setMapBounds, setSearchPoint, updateFilters, loadLocations).
   *
   * Filter precedence:
   * 1. Location type filter (if set)
   * 2. Date filter (if set)
   * 3. Map bounds (viewport) - if user panned/zoomed
   * 4. Search point radius - if search was performed
   */
  updateVisibleLocations: () => {
    // Access cross-slice data via get()
    const state = get() as any; // Cast to access getAllLocations from location-data slice
    const allLocations = state.getAllLocations();
    const { filters, searchPoint, mapBounds, searchMatchIds } = get();

    // First, filter by criteria (location type, date)
    let filtered = allLocations.filter((location: Location) => {
      // Filter by location type
      if (filters.locationType && location.type !== filters.locationType) {
        return false;
      }

      // NOTE: Free-text search has two paths. A query that matches pickup
      // locations by name (e.g. "Lowe's") sets searchMatchIds, applied just
      // below. A query that geocodes to a place (locality/zip/address) sets a
      // searchPoint and is filtered by radius further down. searchQuery itself
      // is only stored for UI display.

      // Filter by date (check if location has a stop on that date)
      if (filters.date) {
        const hasStopOnDate = location.stops.some(
          (stop) => stop.date === filters.date
        );
        if (!hasStopOnDate) {
          return false;
        }
      }

      return true;
    });

    // Name search: restrict membership to the locations that matched by name.
    // This is a multi-result search showing every match sorted by proximity, so
    // both spatial filters below are skipped while it's active — panning the
    // map does not drop far matches from the list.
    if (searchMatchIds) {
      filtered = filtered.filter((location: Location) =>
        searchMatchIds.has(location.id)
      );
    }

    // If mapBounds is set (user panned/zoomed map), filter by radius from center
    // Uses a minimum radius floor to prevent over-filtering at high zoom levels.
    // Skipped during a name search, which shows all matches regardless of view.
    if (mapBounds && !searchMatchIds) {
      const centerLat = (mapBounds.north + mapBounds.south) / 2;
      const centerLng = (mapBounds.east + mapBounds.west) / 2;

      // Derive viewport radius from bounds (distance from center to corner)
      const viewportRadius = calculateDistance(
        centerLat, centerLng,
        mapBounds.north, mapBounds.east
      );
      const effectiveRadius = Math.max(viewportRadius, MIN_VIEWPORT_RADIUS_MILES);

      filtered = filtered.filter((location: Location) => {
        const distance = calculateDistance(
          centerLat, centerLng,
          location.lat, location.lng
        );
        return distance <= effectiveRadius;
      });
    }
    // If searchPoint is set (from geocoded search), filter by proximity.
    // Skipped during a name search, which shows all matches, not a radius.
    else if (searchPoint && !searchMatchIds) {
      const radiusMiles = searchPoint.radiusMiles ?? 50; // Default 50 mile radius

      // Calculate distance for each location and filter by radius
      filtered = filtered.filter((location: Location) => {
        const distance = calculateDistance(
          searchPoint.lat,
          searchPoint.lng,
          location.lat,
          location.lng
        );
        return distance <= radiusMiles;
      });
    }

    // Update the visible IDs Set
    const visibleIds = new Set(filtered.map((loc: Location) => loc.id));
    set({ visibleLocationIds: visibleIds });
  },

  /**
   * Computed function: Get visible locations based on visibleLocationIds Set
   *
   * @remarks
   * Filters all locations to only those whose IDs are in the visibleLocationIds Set.
   * This is an O(n) operation but with O(1) Set lookups, much cheaper than
   * recalculating all filter logic on every call.
   *
   * If searchPoint is set, includes distance calculations and sorts by proximity.
   */
  getVisibleLocations: () => {
    // Access cross-slice data via get()
    const state = get() as any; // Cast to access getAllLocations from location-data slice
    const allLocations = state.getAllLocations();
    const { visibleLocationIds, searchPoint } = get();

    // Filter to only visible locations
    const visible = allLocations.filter((location: Location) =>
      visibleLocationIds.has(location.id)
    );

    // If we have a search point, calculate distances and sort
    if (searchPoint) {
      const locationsWithDistance = visible.map((location: Location) => ({
        location,
        distance: calculateDistance(
          searchPoint.lat,
          searchPoint.lng,
          location.lat,
          location.lng
        ),
      }));

      // Sort by distance (nearest first)
      locationsWithDistance.sort(
        (a: { distance: number }, b: { distance: number }) => a.distance - b.distance
      );

      // Return locations with distance property included
      return locationsWithDistance.map(
        (item: { location: Location; distance: number }) => ({
          ...item.location,
          distance: item.distance,
        })
      );
    }

    return visible;
  },
});
